<template>
  <div class="relative">
    <label v-if="label" class="block uppercase text-blueGray-600 text-xs font-bold mb-2">
      {{ label }}
    </label>

    <!-- Selected user -->
    <div
      v-if="selected"
      class="w-full flex items-center justify-between px-3 py-3 bg-blueGray-50 rounded text-sm border border-blueGray-200"
    >
      <div>
        <span class="font-semibold text-blueGray-700">{{ selected.name }}</span>
        <span class="text-blueGray-400 ml-2">{{ selected.email || selected.phone }}</span>
      </div>
      <button type="button" class="text-blueGray-400 hover:text-red-500 text-xs font-bold" @click="clear">
        Change
      </button>
    </div>

    <!-- Search input + results -->
    <div v-else>
      <input
        v-model="query"
        @input="onInput"
        type="text"
        :placeholder="placeholder"
        class="w-full px-3 py-3 bg-blueGray-50 rounded text-sm border border-blueGray-200 placeholder-blueGray-300 focus:outline-none"
      />

      <div
        v-if="query && (results.length || loaders().searchUsers)"
        class="absolute z-10 w-full mt-1 bg-white rounded shadow-lg border border-blueGray-100 max-h-56 overflow-y-auto"
      >
        <div v-if="loaders().searchUsers" class="px-3 py-3 text-xs text-blueGray-400">
          Searching...
        </div>
        <template v-else>
          <button
            v-for="user in results"
            :key="user.id"
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-blueGray-50 focus:outline-none"
            @click="choose(user)"
          >
            <span class="font-semibold text-blueGray-700">{{ user.name }}</span>
            <span class="text-blueGray-400 ml-2 text-xs">{{ user.email || user.phone }}</span>
          </button>
          <div v-if="!results.length" class="px-3 py-3 text-xs text-blueGray-400">
            No users found.
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'UserSearchSelect',
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Search by name, email or phone…' },
    // Pass the already-chosen user back in (e.g. after a form validation error) so the
    // "selected" state survives — this component doesn't hold selection as its own
    // source of truth, the parent form does.
    modelValue: { type: Object, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      results: [],
      debounceTimer: null,
    };
  },
  computed: {
    selected() {
      return this.modelValue;
    },
  },
  methods: {
    ...mapGetters(['loaders']),
    ...mapActions(['searchUsers']),

    onInput() {
      clearTimeout(this.debounceTimer);
      const q = this.query.trim();
      if (!q) {
        this.results = [];
        return;
      }
      this.debounceTimer = setTimeout(() => {
        this.searchUsers(`search=${encodeURIComponent(q)}`)
          .then(res => {
            // UserController@pubicIndex returns a Laravel paginator — {data: [...],
            // current_page, ...} — not a flat array. Unwrap it, but stay defensive in
            // case the endpoint ever returns a plain array instead.
            this.results = Array.isArray(res) ? res : (res && res.data) || [];
          })
          .catch(() => {
            this.results = [];
          });
      }, 400);
    },

    choose(user) {
      this.$emit('update:modelValue', user);
      this.results = [];
      this.query = '';
    },

    clear() {
      this.$emit('update:modelValue', null);
      this.query = '';
      this.results = [];
    },
  },
};
</script>
